export interface RouteSeoMetadata {
  title: string;
  description: string;
  canonical?: string;
  // Entity pages set their own canonical once data loads (and the meta sidecar
  // injects it server-side), so the route-level SEO must not emit a guessed one.
  omitCanonical?: boolean;
  noIndex?: boolean;
}

const pageMarkdown: Record<string, string> = {
  '/': 'Explore conversations, posts, communities, and perspectives on 4um.',
  '/home': 'Explore conversations, posts, communities, and perspectives on 4um.',
  '/signin': 'Sign in to your 4um account to connect with your communities.',
  '/privacy': 'Read how 4um handles personal data, privacy, security, and user rights.',
  '/aboutus': 'Learn about 4um, an employee-owned and user-guided social media platform.',
  '/groups': 'Discover and participate in communities on 4um.',
  '/chat': 'Connect privately with people and groups on 4um.',
  '/createPost': 'Create and share a post with your 4um community.',
  '/createPoll': 'Create a poll and gather perspectives from your community.',
  '/notification': 'Review your latest 4um notifications and activity.',
  '/search': 'Search 4um for people, groups, posts, and topics.',
  '/consent': 'Review the permissions requested by this 4um connection.'
};

const normalizePath = (pathname: string): string => pathname.replace(/\/$/, '') || '/';

const routeTitleOverrides: Record<string, string> = {
  '/chat': 'Chat',
  '/createPost': 'Create Post',
  '/createPoll': 'Create Poll',
  '/notification': 'Notifications',
  '/consent': 'Consent',
  '/openApp': 'Open App',
  '/groups': 'Groups',
  '/privacy': 'Privacy Policy',
  '/aboutus': 'About Us'
};

export const extractNameFromSlug = (slugPathSegment?: string): string => {
  if (!slugPathSegment) return '';
  const decoded = decodeURIComponent(slugPathSegment).trim();
  if (!decoded) return '';

  const uuidRegex = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  let rawName = decoded;

  if (uuidRegex.test(decoded)) {
    const match = decoded.match(uuidRegex);
    if (match && match.index && match.index > 0) {
      rawName = decoded.slice(0, match.index).replace(/[-_]+$/, '');
    } else {
      return '';
    }
  } else {
    const lastHyphen = decoded.lastIndexOf('-');
    if (lastHyphen > 0) {
      const possibleId = decoded.slice(lastHyphen + 1);
      if (/^\d+$/.test(possibleId) || possibleId.length >= 8) {
        rawName = decoded.slice(0, lastHyphen);
      }
    }
  }

  const cleaned = rawName
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned || /^\d+$/.test(cleaned)) return '';

  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

const getRouteTitle = (pathname: string): string => {
  const normalizedPath = normalizePath(pathname);
  const exactTitle = routeTitleOverrides[normalizedPath];
  if (exactTitle) return exactTitle;

  if (normalizedPath.startsWith('/profile/')) {
    const slugSegment = normalizedPath.replace('/profile/', '');
    const name = extractNameFromSlug(slugSegment);
    return name ? `${name} - 4um Profile` : '4um Profile';
  }

  if (normalizedPath.startsWith('/group/')) {
    const slugSegment = normalizedPath.replace('/group/', '');
    const groupName = extractNameFromSlug(slugSegment);
    return groupName ? `${groupName} Community Group - 4um` : '4um Group';
  }

  const routeSegment = normalizedPath.split('/').filter(Boolean)[0];
  if (!routeSegment) return 'Home';

  return routeSegment
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

export const getPageMarkdown = (pathname: string): string => {
  const normalizedPath = normalizePath(pathname);
  const exactContent = pageMarkdown[normalizedPath];
  if (exactContent) return exactContent;

  if (normalizedPath.startsWith('/post/')) {
    return `## 4um Post

View this community post, its discussion, reactions, and shared media on 4um.

Post URL: ${normalizedPath}`;
  }

  if (normalizedPath.startsWith('/poll/')) {
    return `## 4um Poll

Review this community poll and its responses on 4um.

Poll URL: ${normalizedPath}`;
  }

  if (normalizedPath.startsWith('/profile/')) {
    const slugSegment = normalizedPath.replace('/profile/', '');
    const name = extractNameFromSlug(slugSegment);
    return `## ${name || 'Member Profile'} on 4um

Explore ${name || 'this member'}'s 4um profile, interests, communities, and public activity.

Profile URL: ${normalizedPath}`;
  }

  if (normalizedPath.startsWith('/group/')) {
    const slugSegment = normalizedPath.replace('/group/', '');
    const groupName = extractNameFromSlug(slugSegment);
    return `## ${groupName || 'Community Group'} on 4um

Explore ${groupName || 'this community'} on 4um, its conversations, members, and shared posts.

Group URL: ${normalizedPath}`;
  }

  if (normalizedPath.startsWith('/mediaPost/')) {
    return `## 4um Media Post

View this post and its shared images or videos from the 4um community.

Media post URL: ${normalizedPath}`;
  }

  if (normalizedPath.startsWith('/friendlist/')) {
    return `## Friends on 4um

View the public connections associated with this 4um profile.

Profile URL: ${normalizedPath}`;
  }

  if (normalizedPath.startsWith('/grouplist/')) {
    return `## 4um Group Members

View the communities and group memberships associated with this 4um profile.

Profile URL: ${normalizedPath}`;
  }

  return 'Explore conversations, posts, communities, and perspectives on 4um.';
};

export const getRouteSeo = (pathname: string): RouteSeoMetadata => {
  const normalizedPath = normalizePath(pathname);

  if (normalizedPath === '/' || normalizedPath === '/home') {
    return {
      title: '4um - Connect People and Build Communities Online',
      description: 'Discover communities, join conversations, and connect with people on 4um.',
      canonical: 'https://www.4um.com/'
    };
  }

  if (normalizedPath === '/signin') {
    return {
      title: 'Sign In to 4um',
      description: 'Sign in to 4um to connect with people, share posts, join groups, and take part in community conversations.',
      canonical: 'https://www.4um.com/signin'
    };
  }

  if (normalizedPath === '/aboutus') {
    return {
      title: 'About 4um — Our Story and Community',
      description: 'Learn about 4um, our mission, and how we help people connect, share ideas, and build communities.',
      canonical: 'https://www.4um.com/aboutus'
    };
  }

  if (normalizedPath === '/groups') {
    return {
      title: 'Your Connections — 4um',
      description: 'Explore your connections on 4um. Find people you know, visit their profiles, and stay connected with your community.',
      canonical: 'https://www.4um.com/groups'
    };
  }

  if (normalizedPath === '/chat') {
    return {
      title: 'Chats and Messages — 4um',
      description: 'Keep conversations going on 4um. Send private messages, join group chats, and stay in touch with your connections.',
      canonical: 'https://www.4um.com/chat'
    };
  }

  if (normalizedPath === '/search') {
    return {
      title: 'Search 4um - Find People, Groups, and Posts',
      description: 'Search 4um for people, groups, discussions, posts, and trending topics across communities.',
      canonical: 'https://www.4um.com/search'
    };
  }

  if (normalizedPath === '/createPost') {
    return {
      title: 'Create a Post — 4um',
      description: 'Share your thoughts, updates, photos, or videos with your community on 4um. Start a conversation and connect with others.',
      canonical: 'https://www.4um.com/createPost'
    };
  }

  if (normalizedPath === '/createPoll') {
    return {
      title: 'Create a Poll — 4um',
      description: 'Ask a question, add voting options, and hear from your community. Create a poll on 4um to gather opinions and start discussions.',
      canonical: 'https://www.4um.com/createPoll'
    };
  }

  if (normalizedPath === '/createGroup') {
    return {
      title: 'Create a Group — 4um',
      description: 'Bring people together around shared interests. Create a group on 4um to share updates, exchange ideas, and build a community.',
      canonical: 'https://www.4um.com/createGroup'
    };
  }

  if (normalizedPath === '/notification') {
    return {
      title: 'Your Notifications — 4um',
      description: 'Catch up on your latest notifications on 4um, including mentions, connection requests, and activity involving your posts and groups.',
      canonical: 'https://www.4um.com/notification'
    };
  }

  if (normalizedPath.startsWith('/post/') || normalizedPath.startsWith('/poll/')) {
    return {
      title: getRouteTitle(normalizedPath),
      description: 'Join the discussion on 4um.',
      omitCanonical: true
    };
  }

  if (normalizedPath.startsWith('/profile/')) {
    const slugSegment = normalizedPath.replace('/profile/', '');
    const name = extractNameFromSlug(slugSegment);
    const title = name ? `${name} - 4um Profile` : 'Member Profile - 4um';
    const description = name
      ? `Connect with ${name} on 4um. Explore ${name}'s profile, posts, communities, and public activity on 4um.`
      : 'Connect with people and build communities on 4um.';

    return {
      title,
      description,
      omitCanonical: true
    };
  }

  if (normalizedPath.startsWith('/group/')) {
    const slugSegment = normalizedPath.replace('/group/', '');
    const groupName = extractNameFromSlug(slugSegment);
    const title = groupName ? `${groupName} Community Group - 4um` : 'Community Group - 4um';
    const description = 'Explore community groups and conversations on 4um.';

    return {
      title,
      description,
      omitCanonical: true
    };
  }

  return {
    title: getRouteTitle(normalizedPath),
    description: 'Connect with people and build communities on 4um.',
    canonical: `https://www.4um.com${normalizedPath}`
  };
};

